import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ChefHat, Star, Users, Calendar, ArrowRight, Sparkles, X, Clock, DollarSign, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const FEATURES = [
  {
    img: "https://images.unsplash.com/photo-1757358957218-67e771ec07bb",
    title: "Meal Prep Plans",
    desc: "Customized weekly meal prep tailored to your dietary needs. Fresh, chef-crafted meals delivered to your unit.",
    icon: ChefHat,
    details: {
      headline: "Chef-Crafted Meal Prep, Delivered Weekly",
      description: "Say goodbye to meal planning stress. Chef Kamusa prepares fresh, gourmet-quality meals tailored to your exact dietary preferences and schedule. Every dish is made with premium, locally-sourced ingredients.",
      highlights: [
        { icon: ChefHat, text: "3 Plans: Basic ($150/wk), Standard ($250/wk), Premium ($400/wk)" },
        { icon: Calendar, text: "Flexible scheduling: Daily, Weekdays, or 3x per week" },
        { icon: Heart, text: "Fully customizable dietary preferences & allergies" },
        { icon: Clock, text: "Choose 2, 4, 8, or 12 week durations" },
      ],
      cta: "Start Your Meal Plan",
      ctaLink: "/meal-plans/new",
    },
  },
  {
    img: "https://images.unsplash.com/photo-1767441358044-14d9f8f5b53d",
    title: "In-Home Dining",
    desc: "Book a private chef experience — from romantic date nights to elegant dinner parties in your home.",
    icon: Users,
    details: {
      headline: "Private Chef Experiences in Your Home",
      description: "Transform any evening into something unforgettable. Chef Kamusa comes to your residence to prepare a multi-course meal, handle all setup and cleanup, and create a dining experience tailored to your occasion.",
      highlights: [
        { icon: Heart, text: "Date Night — Intimate dinner for two (from $200)" },
        { icon: Users, text: "Dinner Party — Elevated dining for your guests (from $350)" },
        { icon: Clock, text: "Weekend Brunch — Relaxed morning spread with mimosas (from $180)" },
        { icon: Star, text: "Custom Event — Tell us exactly what you need (from $250)" },
      ],
      cta: "Book a Private Chef",
      ctaLink: "/in-home-booking",
    },
  },
  {
    img: "https://images.unsplash.com/photo-1768508950719-4d76978fdf44",
    title: "Chef Events",
    desc: "Join curated community dinners and exclusive culinary experiences in your building's shared spaces.",
    icon: Calendar,
    details: {
      headline: "Exclusive Community Dining Events",
      description: "Experience themed culinary evenings hosted by Chef Kamusa in your building's premium shared spaces. From Mediterranean nights on the rooftop terrace to intimate Japanese omakase dinners, each event is a unique culinary journey.",
      highlights: [
        { icon: Calendar, text: "Themed events: Mediterranean, Japanese, Farm-to-Table & more" },
        { icon: Users, text: "Intimate gatherings of 10-20 guests per event" },
        { icon: DollarSign, text: "All-inclusive pricing: food, drinks, and the experience" },
        { icon: Star, text: "Premium venues: Rooftop, Garden Lounge, Private Dining" },
      ],
      cta: "Browse Events",
      ctaLink: "/events",
    },
  },
];

export default function Landing() {
  const { demoLogin, user } = useAuth();
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);

  if (user) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      await demoLogin('resident');
      navigate('/dashboard');
    } catch { setDemoLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="glass-header sticky top-0 z-50 border-b border-[#EBEBEB]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#E05A47] flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-bold text-lg tracking-tight text-[#222] font-[Manrope]">My Executive Chef</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-[#717171] font-medium" data-testid="login-nav-btn">
              Sign In
            </Button>
            <Button onClick={() => navigate('/login?tab=register')} className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold btn-press" data-testid="signup-nav-btn">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E05A47]/8 text-[#E05A47] text-xs font-semibold tracking-wide uppercase mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Exclusive to Sol Modern Residents
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#222] leading-[1.08] font-[Manrope]">
                The First<br />
                <span className="text-[#E05A47]">In-Residence</span><br />
                Executive Chef
              </h1>
              <p className="mt-6 text-lg text-[#717171] leading-relaxed max-w-lg">
                A first-of-its-kind private chef program designed exclusively for Sol Modern. Premium meal prep, private dining, and curated culinary events — all without leaving your building.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  onClick={handleDemo}
                  disabled={demoLoading}
                  className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold px-8 h-12 text-base btn-press gap-2"
                  data-testid="demo-resident-btn"
                >
                  {demoLoading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Users className="w-4 h-4" />Try the Demo</>}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="border-[#222] text-[#222] rounded-lg font-semibold px-8 h-12 text-base btn-press gap-2"
                  data-testid="signin-hero-btn"
                >
                  Resident Sign In
                </Button>
              </div>
              <p className="mt-4 text-xs text-[#717171]">Try the full demo instantly — no account needed</p>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_my-chef-web/artifacts/j9hct2ub_IMG_5466.jpeg"
                  alt="Chef Kamusa Som"
                  className="rounded-2xl w-full object-cover aspect-[3/4] shadow-lg"
                  style={{ objectPosition: '50% 55%' }}
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-md rounded-xl p-4 border border-white/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-sm text-[#222] font-[Manrope]">Chef Kamusa Som</p>
                      <p className="text-xs text-[#717171]">Executive Chef · 12 yrs experience</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#E05A47]">Exclusive to</p>
                      <p className="text-xs font-bold text-[#222] font-[Manrope]">Sol Modern</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features / How It Works */}
      <section className="bg-[#F7F7F7] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#222] font-[Manrope]">Your Building. Your Chef.</h2>
            <p className="mt-4 text-[#717171] text-lg">Three exclusive services available only to Sol Modern residents</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {FEATURES.map(({ img, title, desc, icon: Icon }, i) => (
              <motion.button
                key={title}
                onClick={() => setActiveFeature(i)}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl border border-[#EBEBEB] overflow-hidden card-hover text-left cursor-pointer group"
                data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="relative overflow-hidden">
                  <img src={img} alt={title} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-semibold text-[#222]">
                      Learn More
                    </span>
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <div className="w-10 h-10 rounded-xl bg-[#E05A47]/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[#222] font-[Manrope] mb-2">{title}</h3>
                  <p className="text-[#717171] leading-relaxed">{desc}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Detail Dialog */}
      <Dialog open={activeFeature !== null} onOpenChange={(open) => { if (!open) setActiveFeature(null); }}>
        <DialogContent className="sm:max-w-xl p-0 overflow-hidden rounded-2xl" data-testid="feature-popup">
          {activeFeature !== null && (() => {
            const f = FEATURES[activeFeature];
            const d = f.details;
            return (
              <>
                <img src={f.img} alt={f.title} className="w-full h-48 object-cover" />
                <div className="p-6 sm:p-8">
                  <DialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-[#E05A47]/10 flex items-center justify-center">
                        <f.icon className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
                      </div>
                      <DialogTitle className="text-xl font-extrabold text-[#222] font-[Manrope]">{d.headline}</DialogTitle>
                    </div>
                  </DialogHeader>
                  <p className="text-[#717171] leading-relaxed mt-3">{d.description}</p>
                  <div className="mt-6 space-y-3">
                    {d.highlights.map(({ icon: HIcon, text }, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#F7F7F7] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <HIcon className="w-4 h-4 text-[#E05A47]" strokeWidth={1.5} />
                        </div>
                        <p className="text-sm text-[#222] font-medium">{text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 flex gap-3">
                    <Button
                      onClick={() => { setActiveFeature(null); handleDemo(); }}
                      className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold btn-press flex-1"
                      data-testid="feature-popup-cta"
                    >
                      {d.cta} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setActiveFeature(null)}
                      className="rounded-lg font-semibold border-[#EBEBEB]"
                      data-testid="feature-popup-close"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#222] font-[Manrope]">Ready to dine differently?</h2>
          <p className="mt-4 text-[#717171] text-lg max-w-lg mx-auto">As a Sol Modern resident, you have exclusive access to Chef Kamusa's full suite of culinary services. Explore the experience.</p>
          <div className="mt-10 flex justify-center gap-4">
            <Button onClick={handleDemo} disabled={demoLoading} className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold px-8 h-12 btn-press gap-2" data-testid="cta-demo-btn">
              Try the Demo <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#EBEBEB] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-sm text-[#717171]">
          <div className="flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-[#E05A47]" strokeWidth={1.5} />
            <span className="font-medium">My Executive Chef</span>
          </div>
          <span>Exclusively for Sol Modern Residents</span>
        </div>
      </footer>
    </div>
  );
}
